import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertTriangle, Clock, DollarSign, Info, Loader2 } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { IActiveSessionResponse } from '@/services/pos/cashSessionService';

interface SessionRecoveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeSessionData: IActiveSessionResponse['data']['activeSession'] | null;
  onRecoverSession: () => Promise<void>;
  onForceCloseAndCreateNew: () => Promise<void>;
  isLoading?: boolean;
}

export const SessionRecoveryModal: React.FC<SessionRecoveryModalProps> = ({
  isOpen,
  onClose,
  activeSessionData,
  onRecoverSession,
  onForceCloseAndCreateNew,
  isLoading = false
}) => {
  const [selectedAction, setSelectedAction] = useState<'recover' | 'force-close' | null>(null);

  if (!activeSessionData) {
    return null;
  }

  const handleActionSelect = async (action: 'recover' | 'force-close') => {
    setSelectedAction(action);
    
    try {
      if (action === 'recover') {
        await onRecoverSession();
      } else {
        await onForceCloseAndCreateNew();
      }
    } catch (error) {
      setSelectedAction(null);
    }
  };

  const formatDuration = (hours: number) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)} minutos`;
    }
    return `${Math.round(hours * 10) / 10} horas`;
  };

  // Función para obtener datos compatibles entre diferentes estructuras de respuesta
  const getSessionData = () => {
    return {
      id: activeSessionData.id,
      cajeroId: activeSessionData.cajeroId || activeSessionData.userId,
      inicioTurno: activeSessionData.inicioTurno || activeSessionData.startTime,
      efectivoDeclarado: activeSessionData.efectivoDeclarado,
      efectivoCalculado: activeSessionData.efectivoCalculadoSistema || activeSessionData.currentBalance || activeSessionData.efectivoDeclarado,
      horasAbiertas: activeSessionData.horasAbiertas,
      posId: activeSessionData.posId,
      posName: activeSessionData.posName || activeSessionData.pos?.nombre || `POS ${activeSessionData.posId}`
    };
  };

  const sessionData = getSessionData();
  const isSessionOld = sessionData.horasAbiertas > 12;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Sesión de Caja Activa Detectada
          </DialogTitle>
          <DialogDescription>
            Se encontró una sesión de caja abierta anteriormente. 
            Seleccione qué acción desea realizar.
          </DialogDescription>
        </DialogHeader>

        {/* Información de la sesión activa */}
        <Card className="border-orange-200 bg-orange-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              Sesión #{sessionData.id.slice(-8)}
              <Badge 
                variant={isSessionOld ? "destructive" : "secondary"}
                className="ml-2"
              >
                {isSessionOld ? "Sesión Antigua" : "Sesión Reciente"}
              </Badge>
            </CardTitle>
            <CardDescription>
              {sessionData.posName}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Tiempo Abierta</p>
                  <p className="text-sm text-muted-foreground">
                    {formatDuration(sessionData.horasAbiertas)}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Efectivo Declarado</p>
                  <p className="text-sm text-muted-foreground">
                    {formatCurrency(sessionData.efectivoDeclarado)}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Efectivo Calculado</p>
                  <p className="text-sm text-muted-foreground">
                    {formatCurrency(sessionData.efectivoCalculado)}
                  </p>
                </div>
              </div>
            </div>

            <div className="text-xs text-muted-foreground">
              Iniciada: {new Date(sessionData.inicioTurno).toLocaleString()}
            </div>
          </CardContent>
        </Card>

        {isSessionOld && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Esta sesión lleva abierta más de 12 horas. Se recomienda cerrarla y abrir una nueva.
            </AlertDescription>
          </Alert>
        )}

        {/* Opciones de acción */}
        <div className="space-y-4">
          <h4 className="font-medium">¿Qué desea hacer?</h4>
          
          <div className="space-y-3">
            {/* Opción: Continuar sesión */}
            <Card 
              className={`cursor-pointer transition-all ${
                selectedAction === 'recover' 
                  ? 'ring-2 ring-primary bg-primary/5' 
                  : 'hover:bg-muted/50'
              }`}
              onClick={() => !isLoading && handleActionSelect('recover')}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="h-5 w-5 rounded-full border-2 border-primary mt-0.5 flex-shrink-0">
                    {selectedAction === 'recover' && (
                      <div className="h-full w-full rounded-full bg-primary scale-50" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h5 className="font-medium text-sm">Continuar Sesión Anterior</h5>
                    <p className="text-xs text-muted-foreground mt-1">
                      Recuperar la sesión existente y continuar trabajando desde donde se quedó.
                    </p>
                    <Badge variant="outline" className="mt-2 text-xs">
                      Recomendado para sesiones recientes
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Opción: Cerrar anterior y crear nueva */}
            <Card 
              className={`cursor-pointer transition-all ${
                selectedAction === 'force-close' 
                  ? 'ring-2 ring-primary bg-primary/5' 
                  : 'hover:bg-muted/50'
              }`}
              onClick={() => !isLoading && handleActionSelect('force-close')}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="h-5 w-5 rounded-full border-2 border-primary mt-0.5 flex-shrink-0">
                    {selectedAction === 'force-close' && (
                      <div className="h-full w-full rounded-full bg-primary scale-50" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h5 className="font-medium text-sm">Cerrar Anterior y Abrir Nueva</h5>
                    <p className="text-xs text-muted-foreground mt-1">
                      Cerrar automáticamente la sesión anterior e iniciar una nueva sesión.
                    </p>
                    <Badge 
                      variant={isSessionOld ? "default" : "secondary"} 
                      className="mt-2 text-xs"
                    >
                      {isSessionOld ? "Recomendado para sesiones antiguas" : "Usar con precaución"}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription className="text-xs">
            Asegúrese de que no haya otra persona usando la caja antes de cerrar la sesión anterior.
          </AlertDescription>
        </Alert>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button 
            onClick={() => {
              if (selectedAction === 'recover') {
                handleActionSelect('recover');
              } else if (selectedAction === 'force-close') {
                handleActionSelect('force-close');
              }
            }}
            disabled={!selectedAction || isLoading}
          >
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {selectedAction === 'recover' && 'Continuar Sesión'}
            {selectedAction === 'force-close' && 'Cerrar y Crear Nueva'}
            {!selectedAction && 'Seleccione una opción'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
